import message from 'antd/lib/message';
import * as React from 'react';
import injectSheet from 'react-jss';
import {
  getAdditionalChargeColumnConfig,
  getAdditionalChargeLineUploadUrl,
  getAdditionalChargeLines,
  submitAdditionalChargeLines,
  updateAdditionalChargeLines,
  uploadAdditionalChargeFile,
} from 'src/api/additionalCharges';
import { APIResponseType, StylesProps } from 'src/Library/Types';
import { getFileExtension } from 'src/utils/utils';
import { useReduxState } from 'src/hooks/useRedux';
import FileViewer from 'src/components/common/FileViewer';
import {
  buildSaveLines,
  getColumnsForShape,
  getMirrorFieldsForField,
  getVisibleGroups,
  mergeUpdatedLines,
} from './AdditionalChargeColumnRegistry';
import {
  AdditionalChargeCellFunctions,
  AdditionalChargeGridExtras,
  buildAntColumns,
} from './AdditionalChargeGridColumns';
import {
  buildTabExchangeRateLines,
  isLineBlockedFromSubmit,
  lineErrors,
} from './AdditionalChargeResolution';
import {
  ADDITIONAL_CHARGE_GRID_MESSAGES,
  ADDITIONAL_CHARGE_TAB_ORDER,
  TAB_LABELS,
} from './AdditionalChargeGrids.constants';
import { AdditionalChargeMasterOption } from './AdditionalCharges.types';
import {
  AdditionalChargeColumnShape,
  AdditionalChargeDraftAttachments,
  AdditionalChargeGroup,
  AdditionalChargeEditableFieldKey,
  AdditionalChargeLineDraftEdits,
  AdditionalChargeLineDto,
  AdditionalChargeLinesPayload,
  AdditionalChargePendingAttachment,
  AdditionalChargeQuantityDraftInputs,
  AdditionalChargeTabViewModel,
} from './AdditionalChargeGrids.types';
import AdditionalChargeGridsHtml from './AdditionalChargeGrids.html';
import styles from './AdditionalChargeGrids.styles';

interface AdditionalChargeGridsProps extends StylesProps<ReturnType<typeof styles>> {
  taskId: string;
  /** Forces every cell read-only regardless of a line's own status, e.g. a completed / view-only task. */
  isReadOnly?: boolean;
  /**
   * Seeds the first render from an already-fetched lines payload — either the re-entry `GET /lines`
   * check or a `POST /declare` response (D9) — so mounting this component never fires a redundant
   * `GET /lines` for data the caller already has. Consumed exactly once; every subsequent refresh (e.g.
   * after Send for Approval) re-fetches for real.
   */
  initialLinesPayload?: AdditionalChargeLinesPayload;
  /**
   * The charges Part 1 declared, from its own catalog selection. Used only to explain an empty payload
   * (see `declaredGroups`) — server groups always win when present.
   */
  declaredCharges?: AdditionalChargeMasterOption[];
  /** Returns to Part 1 so the vendor can add/remove declared charges. */
  onEditCharges?: () => void;
}

/**
 * Part 2 of the Additional Vendor Charges task (ISS-291749 / C2) — the Incidental and
 * Self-Reimbursement grids the vendor fills in after declaring charges in Part 1.
 *
 * Renders entirely from `GET /additional-charges/column-config` (via `AdditionalChargeColumnRegistry`
 * and `AdditionalChargeGridColumns`) — no per-tab hardcoded column list. The Third-Party Reimbursement
 * tab is hidden (`HIDDEN_CHARGE_TYPES`, D2) until A5 unblocks it; the renderers underneath already cover
 * its two shapes, so unhiding it is a one-line deletion in `AdditionalChargeGrids.constants.ts`.
 */
const AdditionalChargeGrids: React.FC<AdditionalChargeGridsProps> = ({
  classes,
  taskId,
  isReadOnly = false,
  initialLinesPayload,
  declaredCharges,
  onEditCharges,
}) => {
  const [linesPayload, setLinesPayload] = React.useState<AdditionalChargeLinesPayload | undefined>(
    initialLinesPayload
  );
  const [columnShapes, setColumnShapes] = React.useState<AdditionalChargeColumnShape[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>(undefined);

  const [draftEdits, setDraftEdits] = React.useState<AdditionalChargeLineDraftEdits>({});
  // Uploads staged locally (not yet persisted). Held only in component state so an un-saved upload is
  // discarded when the task closes — the grid reverts to its last-saved attachments (ISS-291749).
  const [draftAttachments, setDraftAttachments] = React.useState<AdditionalChargeDraftAttachments>({});
  // ISS-292248 Task 4: staged quantity-dim edits, lineId → dim `code` → typed value — parallel to
  // `draftEdits` but kept separate (dim codes are dynamic per charge, not a closed column-key union). A
  // PATCH re-resolves the line server-side (F4), so on success this is cleared for every saved line the
  // same way `draftEdits`/`draftAttachments` are — the server's `resolution.quantity` is authoritative
  // from that point on, not whatever the vendor was mid-typing.
  const [draftQuantityInputs, setDraftQuantityInputs] = React.useState<AdditionalChargeQuantityDraftInputs>(
    {}
  );
  const [checkedLineIds, setCheckedLineIds] = React.useState<Record<string, boolean>>({});
  const [uploadingLineIds, setUploadingLineIds] = React.useState<Record<string, boolean>>({});
  const [isSaving, setIsSaving] = React.useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const [activeTabKey, setActiveTabKey] = React.useState<string | undefined>(undefined);
  // Which attachment (saved or staged) the in-app FileViewer is previewing; undefined = closed.
  const [fileToPreview, setFileToPreview] = React.useState<
    { url: string; title: string; fileName: string } | undefined
  >(undefined);

  // Consumed exactly once — a forced refresh (e.g. post-submit) must always hit the real endpoint.
  const hasConsumedInitialPayloadRef = React.useRef<boolean>(false);

  /**
   * The currency picker's option list (ISS-292248, P9) — the same master-data currency source
   * `useCurrency` reads (`src/hooks/fields/invoicing/useCurrency.ts`), not a re-fetch of our own. Not
   * part of the lines payload, so it is threaded into `gridExtras` separately below.
   */
  const currencySymbols = useReduxState(['masterData', 'currencySymbols']).value;
  const currencyOptions = React.useMemo(
    () => (currencySymbols ? Object.keys(currencySymbols) : []),
    [currencySymbols]
  );

  const fetchAll = async (forceLinesRefetch = false) => {
    setIsLoading(true);
    const useSeededLines = !!initialLinesPayload && !hasConsumedInitialPayloadRef.current && !forceLinesRefetch;
    hasConsumedInitialPayloadRef.current = true;

    const seededLinesResponse: APIResponseType<AdditionalChargeLinesPayload> = {
      isSuccess: true,
      data: initialLinesPayload,
    };
    const [columnConfigResponse, linesResponse] = await Promise.all([
      getAdditionalChargeColumnConfig(),
      // D9: the seeded payload is reused as-is here, exactly once — no re-fetch.
      useSeededLines ? Promise.resolve(seededLinesResponse) : getAdditionalChargeLines({ taskId }),
    ]);

    if (columnConfigResponse.isSuccess && linesResponse.isSuccess) {
      setColumnShapes(columnConfigResponse.data || []);
      setLinesPayload(linesResponse.data);
      setErrorMessage(undefined);
    } else {
      const failure =
        columnConfigResponse.errorMessage ||
        linesResponse.errorMessage ||
        ADDITIONAL_CHARGE_GRID_MESSAGES.fetchLinesFailed;
      message.error(failure);
      setErrorMessage(failure);
    }
    setIsLoading(false);
  };

  React.useEffect(() => {
    if (!taskId) return;
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  /**
   * Fans one edit out to every field in its mirror group (ISS-292248, e.g. `gstPair` on CGST/SGST) —
   * `getMirrorFieldsForField` returns just `[field]` for an ungrouped column, so this is a no-op
   * generalisation of the old single-field write, not a special case for GST. Both mirrored fields land
   * in the SAME state update, so the next render (and the next Save/Submit patch) always carries both.
   */
  const onFieldChange = (
    lineId: string,
    field: AdditionalChargeEditableFieldKey,
    value: string | null
  ) => {
    const fields = getMirrorFieldsForField(columnShapes, field) as AdditionalChargeEditableFieldKey[];
    setDraftEdits((current) => ({
      ...current,
      [lineId]: fields.reduce(
        (draft, mirroredField) => ({ ...draft, [mirroredField]: value }),
        { ...(current[lineId] || {}) }
      ),
    }));
  };

  /** ISS-292248 Task 4: stages one quantity-dim edit. Counts as a "touch" for `buildSaveLines`'s
   * skip-untouched rule the same way `onFieldChange`'s staged edits and a staged attachment do — see
   * `buildSaveLines`'s `hasDraftQuantity` check. */
  const onQuantityInputChange = (lineId: string, dimCode: string, value: string) => {
    setDraftQuantityInputs((current) => ({
      ...current,
      [lineId]: { ...(current[lineId] || {}), [dimCode]: value },
    }));
  };

  const onToggleCheck = (lineId: string, checked: boolean) => {
    setCheckedLineIds((current) => ({ ...current, [lineId]: checked }));
  };

  const onSelectedRowKeysChange = (lineIds: string[], selectedKeys: string[]) => {
    const selectedSet = new Set(selectedKeys);
    setCheckedLineIds((current) => {
      const next = { ...current };
      lineIds.forEach((lineId) => {
        next[lineId] = selectedSet.has(lineId);
      });
      return next;
    });
  };

  const onSaveDraft = async () => {
    // "Save" overrides the server with whatever the grid currently shows for a line the vendor actually
    // touched this session — every touched line's current value plus any newly-staged attachment —
    // rather than diffing only the changed field (ISS-291749). A line with nothing staged is skipped
    // entirely (ISS-292248 review fix, IMPORTANT 3a), so clicking Save with no edits at all is a silent
    // no-op (`buildSaveLines` returns `[]`, and the guard below returns early), not a warning.
    const groups = linesPayload ? getVisibleGroups(linesPayload.groups) : [];
    const lines = buildSaveLines(
      groups,
      columnShapes,
      draftEdits,
      draftAttachments,
      isReadOnly,
      linesPayload?.thirdPartyVendors || [],
      draftQuantityInputs
    );
    if (!lines.length) {
      return;
    }
    // Snapshot exactly what this Save is persisting. Only these line entries — and only if still
    // untouched when the response returns — get cleared on success; anything the vendor stages AFTER
    // clicking Save (a re-edit replaces the entry with a fresh object reference) must survive, not be
    // wiped by a blanket reset (Codex review P2).
    const savedLineIds = new Set(lines.map((saveLine) => saveLine.id));
    const savedDraftEdits = draftEdits;
    const savedDraftAttachments = draftAttachments;
    const savedDraftQuantityInputs = draftQuantityInputs;
    const clearSavedUntouched = <T,>(current: Record<string, T>, saved: Record<string, T>): Record<string, T> => {
      const next = { ...current };
      savedLineIds.forEach((lineId) => {
        // Same reference => not re-edited since we snapshotted for this Save => safe to clear. A newer
        // edit produced a new reference and is retained.
        if (Object.prototype.hasOwnProperty.call(next, lineId) && next[lineId] === saved[lineId]) {
          delete next[lineId];
        }
      });
      return next;
    };
    setIsSaving(true);
    const response = await updateAdditionalChargeLines({ lines });
    if (response.isSuccess && linesPayload) {
      setLinesPayload(mergeUpdatedLines(linesPayload, response.data?.lines || []));
      // Everything just-saved was just persisted, so its local draft entries are clean. The re-resolved
      // lines merged in above carry the server's own `resolution.quantity` (ISS-292248, F4) — authoritative
      // from here. Clear ONLY the saved-and-still-untouched entries so a dim edit staged mid-request lives on.
      setDraftEdits((current) => clearSavedUntouched(current, savedDraftEdits));
      setDraftAttachments((current) => clearSavedUntouched(current, savedDraftAttachments));
      setDraftQuantityInputs((current) => clearSavedUntouched(current, savedDraftQuantityInputs));
      message.success(ADDITIONAL_CHARGE_GRID_MESSAGES.saveDraftSuccess);
      // ISS-292248 (CRITICAL 1c): the bulk-PATCH response above carries only the updated LINES, not a
      // refreshed `exchangeRates`/`pcd` — a currency the vendor just picked (or a PCD shift) only shows
      // up on a real GET. Reuse the same force-refetch path Submit uses below, rather than inventing a
      // second one. Anything staged AFTER this point lives in draftEdits/draftAttachments (already
      // cleared above), which this refetch never touches, so it can't clobber a fresh in-flight edit.
      await fetchAll(true);
    } else {
      message.error(response.errorMessage || ADDITIONAL_CHARGE_GRID_MESSAGES.saveDraftFailed);
    }
    setIsSaving(false);
  };

  const onSubmitForApproval = async () => {
    const lineIds = Object.keys(checkedLineIds).filter((lineId) => checkedLineIds[lineId]);
    if (!lineIds.length) {
      message.error(ADDITIONAL_CHARGE_GRID_MESSAGES.noRowsSelected);
      return;
    }

    // ISS-292248 Task 5: block Send for Approval client-side before the API call when any TICKED line
    // still carries a rate-engine error, or an unpinned open-ended quantity dim, per
    // `isLineBlockedFromSubmit` — submitting it would only fail later, server-side, after the round
    // trip. A line with nothing wrong submits exactly as it did before this task.
    const groups = linesPayload ? getVisibleGroups(linesPayload.groups) : [];
    const checkedLines = groups
      .flatMap((group) => group.charges)
      .flatMap((charge) => charge.lines)
      .filter((line) => checkedLineIds[line.id]);
    const firstBlockedLine = checkedLines.find((line) => isLineBlockedFromSubmit(line));
    if (firstBlockedLine) {
      const firstErrorMessage =
        lineErrors(firstBlockedLine)[0]?.message ?? ADDITIONAL_CHARGE_GRID_MESSAGES.lineBlockedOpenEndedQuantity;
      message.error(
        ADDITIONAL_CHARGE_GRID_MESSAGES.lineBlockedFromSubmit(firstBlockedLine.lineReferenceNumber, firstErrorMessage)
      );
      return;
    }

    setIsSubmitting(true);
    // D1: POST /tasks/:taskId/submit is contract-only until B2 ships. Wired fully regardless — the
    // failure surfaces through the same message.error path as any other API call, with no special case.
    const response = await submitAdditionalChargeLines({ taskId, lineIds });
    if (response.isSuccess) {
      message.success(ADDITIONAL_CHARGE_GRID_MESSAGES.submitSuccess);
      setCheckedLineIds({});
      await fetchAll(true); // force a real GET /lines — never replay the seeded payload here
    } else {
      message.error(response.errorMessage || ADDITIONAL_CHARGE_GRID_MESSAGES.submitFailed);
    }
    setIsSubmitting(false);
  };

  const onUploadAttachment = async (line: AdditionalChargeLineDto, file: File) => {
    setUploadingLineIds((current) => ({ ...current, [line.id]: true }));
    const extension = getFileExtension(file.name);
    const presignResponse = await getAdditionalChargeLineUploadUrl({ lineId: line.id, extension });

    if (!presignResponse.isSuccess || !presignResponse.data) {
      message.error(presignResponse.errorMessage || ADDITIONAL_CHARGE_GRID_MESSAGES.uploadFailed);
      setUploadingLineIds((current) => ({ ...current, [line.id]: false }));
      return;
    }

    const { get } = presignResponse.data;
    const uploadResponse = await uploadAdditionalChargeFile(presignResponse.data.post, extension, file);
    if (!uploadResponse.isSuccess) {
      message.error(uploadResponse.errorMessage || ADDITIONAL_CHARGE_GRID_MESSAGES.uploadFailed);
      setUploadingLineIds((current) => ({ ...current, [line.id]: false }));
      return;
    }

    // Keep the ORIGINAL uploaded file name (`file.name`), not the presign's line-reference name — the
    // backend stores whatever `fileName` we send here as the document name and echoes it back on GET
    // /lines, so the vendor sees "invoice.xlsx", not "ACL1". Carrying the real extension is also what
    // lets the preview pick the right viewer.
    //
    // The file is now in S3 but NOT persisted to the line yet (ISS-291749). It rides along on the next
    // Save as Draft (see `buildSaveLines`), so a file uploaded and then closed without saving is
    // discarded and the grid reverts to its last-saved attachment.
    setDraftAttachments((current) => ({ ...current, [line.id]: { url: get, fileName: file.name } }));
    setUploadingLineIds((current) => ({ ...current, [line.id]: false }));
  };

  const onOpenAttachment = (attachment: AdditionalChargePendingAttachment) => {
    // In-app preview via the shared FileViewer drawer — the same pattern every other SE task uses —
    // rather than opening a raw S3 tab. FileViewer picks its renderer from the file extension, but our
    // attachment `fileName` is the line reference number (e.g. "ACL1") with NO extension, so it would
    // always fall to the PDF viewer and render an Excel/image as a blank doc. The real extension lives in
    // the S3 URL path (".../ACL1.xlsx?X-Amz-..."), so derive it there (query stripped) and hand
    // FileViewer a name that carries it. `title` stays the clean reference for the drawer header.
    const urlPath = attachment.url.split(/[?#]/)[0];
    const urlExtension = getFileExtension(urlPath);
    const hasOwnExtension = getFileExtension(attachment.fileName) !== '';
    const fileNameForType =
      hasOwnExtension || !urlExtension ? attachment.fileName : `${attachment.fileName}.${urlExtension}`;
    setFileToPreview({ url: attachment.url, title: attachment.fileName, fileName: fileNameForType });
  };

  const cellFunctions: AdditionalChargeCellFunctions = {
    onFieldChange,
    onUploadAttachment,
    onOpenAttachment,
    getPendingAttachment: (lineId: string) => draftAttachments[lineId],
    isUploadingLine: (lineId: string) => !!uploadingLineIds[lineId],
    onQuantityInputChange,
  };

  const serverGroups = linesPayload ? getVisibleGroups(linesPayload.groups) : [];

  /**
   * Fed straight from the lines payload (ISS-292248) into every cell's `CellRenderContext` via
   * `buildAntColumns`'s `extras` param. `currencyOptions` is sourced from the master-data
   * `currencyOptions` memo above, and the two `*SelectClassName` entries from this component's own
   * `classes` (ISS-292248 review fix, MINOR 8) — neither is part of the lines payload.
   */
  const gridExtras: AdditionalChargeGridExtras = {
    pcd: linesPayload?.pcd ?? null,
    // ISS-292248 Task 3: threaded straight from the payload alongside `pcd` — `resolveDisplayPcd` reads
    // it for the CONTAINER-level PCD fallback (the PCD cell via `renderPcd`).
    pcdByContainer: linesPayload?.pcdByContainer,
    exchangeRates: linesPayload?.exchangeRates || {},
    thirdPartyVendors: linesPayload?.thirdPartyVendors || [],
    currencyOptions,
    currencySelectClassName: classes.currencySelect,
    thirdPartyVendorSelectClassName: classes.thirdPartyVendorSelect,
  };

  /**
   * Fan-out produced nothing for a charge the vendor did declare — the shipment has no BL (BL-level) or
   * no containers (CONTAINER-level). `POST /declare` reports that as a 200 with `groups: []`
   * (ISS-291742), so the declaration is reconstructed from Part 1's own selection and rendered as empty
   * sections carrying the reason. Without this the vendor lands on "nothing was declared", which is
   * both wrong and unactionable.
   */
  const declaredGroups: AdditionalChargeGroup[] = React.useMemo(() => {
    if (serverGroups.length || !declaredCharges?.length) return [];
    return getVisibleGroups(
      ADDITIONAL_CHARGE_TAB_ORDER.filter((chargeType) =>
        declaredCharges.some((charge) => charge.chargeType === chargeType)
      ).map((chargeType) => ({
        chargeType,
        charges: declaredCharges
          .filter((charge) => charge.chargeType === chargeType)
          .map((charge) => ({
            chargeCode: charge.chargeCode,
            chargeName: charge.chargeName,
            applicabilityLevel: charge.applicabilityLevel,
            sacCode: charge.sacCode ?? null,
            isAttachmentMandatory: charge.isAttachmentMandatory,
            lines: [],
          })),
      }))
    );
  }, [serverGroups.length, declaredCharges]);

  const visibleGroups = serverGroups.length ? serverGroups : declaredGroups;

  const tabs: AdditionalChargeTabViewModel[] = ADDITIONAL_CHARGE_TAB_ORDER.filter((chargeType) =>
    visibleGroups.some((group) => group.chargeType === chargeType)
  ).map((chargeType) => {
    const group = visibleGroups.find((candidate) => candidate.chargeType === chargeType);
    const sections = (group?.charges || []).map((charge) => {
      const columns = getColumnsForShape(columnShapes, chargeType, charge.applicabilityLevel);
      const antColumns = buildAntColumns(
        columns,
        draftEdits,
        cellFunctions,
        charge.isAttachmentMandatory,
        gridExtras,
        isReadOnly,
        draftQuantityInputs
      );
      return {
        chargeCode: charge.chargeCode,
        chargeName: charge.chargeName,
        applicabilityLevel: charge.applicabilityLevel,
        isAttachmentMandatory: charge.isAttachmentMandatory,
        lines: charge.lines,
        columns: antColumns,
        selectedRowKeys: charge.lines
          .map((line) => line.id)
          .filter((lineId) => checkedLineIds[lineId]),
        emptyReason: charge.lines.length
          ? undefined
          : charge.applicabilityLevel === 'BL'
          ? ADDITIONAL_CHARGE_GRID_MESSAGES.noBlFound
          : ADDITIONAL_CHARGE_GRID_MESSAGES.noContainersFound,
      };
    });
    // Footer rows are per tab and driven by the currencies THIS tab actually uses — the payload's rate
    // map holds every pickable currency, so rendering it wholesale would list ~20 unused rates. Each line
    // contributes its own `(currency, rate, pcd)` triple, so CONTAINER-level tabs whose containers froze at
    // different PCDs surface a distinct row each rather than one flat payload rate (ISS-292248 F6/F7).
    const exchangeRateLines = buildTabExchangeRateLines(
      sections,
      draftEdits,
      gridExtras.exchangeRates,
      gridExtras.pcd,
      gridExtras.pcdByContainer
    );
    return { chargeType, label: TAB_LABELS[chargeType], sections, exchangeRateLines };
  });

  const resolvedActiveTabKey = activeTabKey || tabs[0]?.chargeType;

  return (
    <>
      {AdditionalChargeGridsHtml({
        classes,
        isLoading,
        errorMessage,
        isReadOnly,
        tabs,
        activeTabKey: resolvedActiveTabKey,
        isSaving,
        isSubmitting,
        onTabChange: setActiveTabKey,
        onRowSelectionChange: onSelectedRowKeysChange,
        onSaveDraft,
        onSubmitForApproval,
        onEditCharges,
      })}
      {fileToPreview && (
        <FileViewer
          url={fileToPreview.url}
          title={fileToPreview.title}
          fileName={fileToPreview.fileName}
          onClose={() => setFileToPreview(undefined)}
        />
      )}
    </>
  );
};

export default injectSheet(styles)(AdditionalChargeGrids);
